import styled, { createGlobalStyle } from "styled-components";

/**
 * Lovable index.css tokens as CSS variables on .homepage-page.
 * Switches with html[data-theme="light"|"dark"]. Content stays ours;
 * only colors/surfaces follow Lovable light + dark.
 */
const homepageThemeVars = `
  /* Dark (default — matches Lovable .dark) */
  --hp-background: 230 25% 5%;
  --hp-foreground: 40 20% 95%;
  --hp-card: 230 20% 8%;
  --hp-card-foreground: 40 20% 95%;
  --hp-primary: 32 95% 55%;
  --hp-primary-foreground: 230 25% 5%;
  --hp-secondary: 230 15% 15%;
  --hp-secondary-foreground: 40 20% 95%;
  --hp-muted: 230 15% 12%;
  --hp-muted-foreground: 230 10% 55%;
  --hp-accent: 32 80% 45%;
  --hp-accent-foreground: 40 20% 95%;
  --hp-popover: 230 20% 8%;
  --hp-popover-foreground: 40 20% 95%;
  --hp-border: 230 15% 18%;
  --hp-gold: 40 90% 60%;
  --hp-gold-dim: 40 70% 40%;
  --hp-surface-glass: 230 20% 10%;
  --hp-gradient-gold: linear-gradient(135deg, hsl(32 95% 55%) 0%, hsl(40 90% 60%) 100%);
  --hp-gradient-dark: linear-gradient(180deg, hsl(230 25% 5%) 0%, hsl(230 20% 8%) 100%);
  --hp-gradient-card: linear-gradient(145deg, hsl(230 20% 10%) 0%, hsl(230 20% 6%) 100%);
  --hp-gradient-hero: radial-gradient(ellipse at 50% 0%, hsl(32 95% 55% / 0.15) 0%, transparent 60%);
  --hp-shadow-glow: 0 0 60px hsl(32 95% 55% / 0.3);
  --hp-shadow-card: 0 4px 30px hsl(0 0% 0% / 0.3);
  --hp-shadow-button: 0 4px 20px hsl(32 95% 55% / 0.4);
  --hp-radius: 0.75rem;
  --hp-radius-md: calc(var(--hp-radius) - 2px);
  --hp-ring: 32 95% 55%;

  html[data-theme="light"] & {
    /* Light — matches Lovable :root */
    --hp-background: 40 30% 99%;
    --hp-foreground: 230 25% 10%;
    --hp-card: 0 0% 100%;
    --hp-card-foreground: 230 25% 10%;
    --hp-primary: 32 95% 48%;
    --hp-primary-foreground: 0 0% 100%;
    --hp-secondary: 40 25% 94%;
    --hp-secondary-foreground: 230 25% 12%;
    --hp-muted: 40 20% 93%;
    --hp-muted-foreground: 230 10% 40%;
    --hp-accent: 32 85% 45%;
    --hp-accent-foreground: 0 0% 100%;
    --hp-popover: 0 0% 100%;
    --hp-popover-foreground: 230 25% 10%;
    --hp-border: 230 12% 88%;
    --hp-gold: 40 85% 45%;
    --hp-gold-dim: 40 70% 38%;
    --hp-surface-glass: 0 0% 100%;
    --hp-gradient-gold: linear-gradient(135deg, hsl(32 95% 48%) 0%, hsl(40 85% 45%) 100%);
    --hp-gradient-dark: linear-gradient(180deg, hsl(40 30% 99%) 0%, hsl(40 25% 95%) 100%);
    --hp-gradient-card: linear-gradient(145deg, hsl(0 0% 100%) 0%, hsl(40 25% 97%) 100%);
    --hp-gradient-hero: radial-gradient(ellipse at 50% 0%, hsl(32 95% 55% / 0.18) 0%, transparent 60%);
    --hp-shadow-glow: 0 0 60px hsl(32 95% 55% / 0.18);
    --hp-shadow-card: 0 4px 30px hsl(230 25% 10% / 0.08);
    --hp-shadow-button: 0 4px 20px hsl(32 95% 55% / 0.3);
    --hp-ring: 32 95% 48%;
  }
`;

/** Theme values — resolve from CSS vars so light/dark switch automatically. */
export const theme = {
  bg: "hsl(var(--hp-background))",
  foreground: "hsl(var(--hp-foreground))",
  card: "hsl(var(--hp-card))",
  muted: "hsl(var(--hp-muted))",
  mutedForeground: "hsl(var(--hp-muted-foreground))",
  border: "hsl(var(--hp-border))",
  borderSoft: "hsl(var(--hp-border) / 0.5)",
  primary: "hsl(var(--hp-primary))",
  primaryForeground: "hsl(var(--hp-primary-foreground))",
  gold: "hsl(var(--hp-gold))",
  radius: "12px",
  radiusLg: "0.75rem",
  font: "'Outfit', system-ui, sans-serif",
  gradientGold: "var(--hp-gradient-gold)",
  gradientHero: "var(--hp-gradient-hero)",
  gradientDark: "var(--hp-gradient-dark)",
  gradientCard: "var(--hp-gradient-card)",
  shadowGlow: "var(--hp-shadow-glow)",
  shadowButton: "var(--hp-shadow-button)",
  shadowCard: "var(--hp-shadow-card)",
  maxWidth: "1200px",
  bgElevated: "hsl(var(--hp-card))",
  bgCard: "hsl(var(--hp-card))",
  text: "hsl(var(--hp-foreground))",
  textMuted: "hsl(var(--hp-muted-foreground))",
  textSoft: "hsl(var(--hp-muted-foreground))",
  accent: "hsl(var(--hp-accent))",
  accentBright: "hsl(var(--hp-gold))",
  accentGlow: "hsl(var(--hp-primary) / 0.4)",
  glassSurface: "hsl(var(--hp-card) / 0.8)",
  secondary: "hsl(var(--hp-secondary))",
  secondarySoft: "hsl(var(--hp-secondary) / 0.3)",
  secondarySofter: "hsl(var(--hp-secondary) / 0.2)",
};

export const typography = {
  fontFamily: theme.font,
  weights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  tracking: {
    tight: "-0.025em",
    tighter: "-0.03em",
    tightest: "-0.04em",
    normal: "0",
    wide: "0.08em",
  },
};

export const primaryAccentGradientTextStyles = `
  background-image: linear-gradient(to right, ${theme.primary}, ${theme.accent});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

export const gradientTextStyles = `
  background-image: ${theme.gradientGold};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

export const HomepageGlobalStyles = createGlobalStyle`
  @import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap");

  body {
    font-family: ${typography.fontFamily};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: ${typography.fontFamily};
  }
`;

/** Scope homepage CSS vars for portals rendered outside `Page` (e.g. body). */
export const HomepageThemeScope = styled.div`
  ${homepageThemeVars}
  font-family: ${typography.fontFamily};
  color: ${theme.foreground};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

export const Page = styled.div`
  ${homepageThemeVars}
  min-height: 100vh;
  background: ${theme.bg};
  background-image: ${theme.gradientDark};
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 16px;
  font-weight: ${typography.weights.normal};
  letter-spacing: ${typography.tracking.normal};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;

  && h1,
  && h2,
  && h3,
  && h4,
  && h5,
  && h6 {
    font-family: ${typography.fontFamily};
    letter-spacing: normal;
  }

  p,
  a,
  button,
  span,
  li,
  label {
    font-family: inherit;
  }

  .material-icons {
    font-family: "Material Icons";
  }
`;

export const Section = styled.section`
  position: relative;
  padding: ${({ $compact }) => ($compact ? "40px 16px" : "56px 16px")};

  @media (min-width: 768px) {
    padding: ${({ $compact }) => ($compact ? "48px 24px" : "80px 24px")};
  }
`;

export const Container = styled.div`
  width: 100%;
  max-width: ${theme.maxWidth};
  margin: 0 auto;
`;

export const SectionTitle = styled.h2`
  margin: 0 0 16px;
  color: ${theme.text};
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  font-weight: ${typography.weights.extrabold};
  line-height: 1.15;
  text-align: center;

  span {
    background-image: ${theme.gradientGold};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

export const SectionLead = styled.p`
  margin: 0 auto 48px;
  max-width: 720px;
  color: ${theme.textMuted};
  font-size: 1.0625rem;
  line-height: 1.65;
  text-align: center;
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 28px;
  border: none;
  border-radius: 999px;
  background-image: ${theme.gradientGold};
  box-shadow: ${theme.shadowButton};
  color: ${theme.primaryForeground};
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: ${typography.weights.bold};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 6px 30px hsl(var(--hp-primary) / 0.5);
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 3px;
  }
`;

/** glass-card utility from Lovable: bg-card/80 backdrop-blur-xl border-border/50 */
export const glassCardStyles = `
  background: ${theme.glassSurface};
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid ${theme.borderSoft};
  box-shadow: ${theme.shadowCard};
`;
